import React, { useEffect, useRef } from "react"
import gsap from "gsap"
import styled from "styled-components"
import media from "styles/media"
import colors from "styles/colors"
import text from "styles/text"
import ContentWidth from "components/ContentWidth"
import Squirqle from "images/quote-squerkle.svg"
import { ReactComponent as Arrow } from "images/new-hp-arrow-btn.svg"
import { horizontalLoop } from "utils/horizontalLoop"
import StyledLink from "components/StyledLink"

const TestimonialCarousel = ({ content }) => {
  const awaitRef = useRef(0)

  useEffect(() => {
    const testimonialsArr = gsap.utils.toArray(".testimonials")
    const testimonialLoop = horizontalLoop(testimonialsArr, { paused: true })
    gsap.set("#test-0", { opacity: 1 })
    const testimonailTL = gsap.timeline({
      onComplete: () => {
        awaitRef.current = 1
      },
    })

    document.querySelector(".next").addEventListener("click", () => {
      testimonailTL
        .call(() => {
          testimonialLoop.next({ duration: 0.55, ease: "slow" })
        })
        .to(
          `#test-${testimonialLoop.current()}`,
          { opacity: 0, duration: 0.25 },
          ">"
        )
        .set(
          `#test-${testimonialLoop.current()}`,
          {
            opacity: 1,
          },
          ">"
        )
    })
    document.querySelector(".prev").addEventListener("click", () => {
      testimonailTL
        .call(() => {
          testimonialLoop.previous({ duration: 0.55, ease: "slow" })
        })
        .to(
          `#test-${testimonialLoop.current()}`,
          { opacity: 0, duration: 0.25 },
          ">+=25%"
        )
        .set(
          `#test-${testimonialLoop.current()}`,
          {
            opacity: 1,
          },
          "<"
        )
    })
  }, [])

  const testimonials = content.quotes.map((testimonial, index) => (
    <TestimonialDiv
      id={`test-${index}`}
      key={testimonial.quote}
      className="testimonials"
    >
      <Testimonial mode={content.mode}>
        <TestimonialBody>“{testimonial.quote}”</TestimonialBody>
        <InfoDiv>
          <TestimonialAuthor>{testimonial.author}</TestimonialAuthor>
          <TestimonialPosition>{testimonial.position}</TestimonialPosition>
          <TestimonialLocation>{testimonial.location}</TestimonialLocation>
        </InfoDiv>
      </Testimonial>
    </TestimonialDiv>
  ))

  return (
    <Wrapper mode={content.mode}>
      <ContentWidth>
        <AroundContainer>
          <ArrowWrapper>
            <LeftArrow className="prev" />
            <RightArrow className="next" />
          </ArrowWrapper>
          <Eyebrow mode={content.mode}>TESTIMONIAL</Eyebrow>
          <CarouselContainer>
            <TestimonialsContainer>{testimonials}</TestimonialsContainer>
          </CarouselContainer>
          {content.linkSrc && (
            <LinkContainer>
              <StyledLink href={content.linkSrc}>{content.linkText}</StyledLink>
            </LinkContainer>
          )}
        </AroundContainer>
        <DesignImage alt="squircle" src={Squirqle} />
      </ContentWidth>
    </Wrapper>
  )
}

export default TestimonialCarousel

const AroundContainer = styled.div`
  justify-self: flex-start;
`
const LinkContainer = styled.div`
  width: 80.361vw;

  ${media.fullWidth} {
    width: 350px;
  }
  ${media.tablet} {
    width: 24.414vw;
  }
  ${media.mobile} {
    width: 50.411vw;
  }
`
const Eyebrow = styled.p`
  ${text.eyebrow};
  text-align: left;
  margin-top: 1.389vw;
  margin-bottom: 3.333vw;
  color: ${props => (props.mode === "dark" ? colors.white : colors.grey800)};

  ${media.fullWidth} {
    margin-top: 20px;
    margin-bottom: 48px;
  }
  ${media.tablet} {
    margin-top: 0.977vw;
    margin-bottom: 4.688vw;
  }
  ${media.mobile} {
    margin-top: 3.738vw;
    margin-bottom: 9.346vw;
  }
`
const ArrowWrapper = styled.div`
  width: 100%;
  display: flex;
  gap: 1.667vw;
  justify-content: flex-end;
  align-items: flex-end;

  ${media.fullWidth} {
    gap: 24px;
    width: 1144px;
  }
  ${media.tablet} {
    gap: 2.344vw;
    width: 100%;
  }
  ${media.mobile} {
    gap: 4.607vw;
    width: 90%;
  }
`
const RightArrow = styled(Arrow)`
  width: 2.083vw;
  cursor: pointer;

  ${media.fullWidth} {
    width: 30px;
  }

  ${media.tablet} {
    width: 2.93vw;
  }

  ${media.mobile} {
    width: 3.505vw;
  }
`
const LeftArrow = styled(Arrow)`
  transform: rotate(180deg);
  width: 2.083vw;
  cursor: pointer;

  ${media.fullWidth} {
    width: 30px;
  }

  ${media.tablet} {
    width: 2.93vw;
  }

  ${media.mobile} {
    width: 3.505vw;
  }
`
const TestimonialAuthor = styled.p`
  ${text.bodyMdBold};

  ${media.mobile} {
    ${text.bodySm};
  }
`
const TestimonialPosition = styled.p`
  ${text.bodyMd};

  ${media.mobile} {
    ${text.bodySm};
  }
`
const TestimonialLocation = styled.p`
  ${text.bodyMd};

  ${media.mobile} {
    ${text.bodySm};
  }
`
const InfoDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  margin-top: auto;
  margin-left: 2.083vw;

  ${media.fullWidth} {
    margin-left: 30px;
  }

  ${media.tablet} {
    margin-left: 2.93vw;
  }

  ${media.mobile} {
    margin-left: 7.009vw;
  }
`
const TestimonialBody = styled.blockquote`
  ${text.h2};
  width: 71.389vw;
  text-indent: -1.389vw;
  transform: translateX(2.083vw);
  margin-bottom: 8.417vw;

  ${media.fullWidth} {
    width: 1028px;
    text-indent: -20px;
    transform: translateX(30px);
    margin-bottom: 121px;
  }
  ${media.tablet} {
    width: 75.199vw;
    text-indent: -1.953vw;
    transform: translateX(2.93vw);
    margin-bottom: 12.895vw;
  }

  ${media.mobile} {
    ${text.h3};
    width: 77.383vw;
    text-indent: -2.673vw;
    transform: translateX(8.509vw);
    margin-bottom: 20.776vw;
  }
`
const Testimonial = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.667vw;
  color: ${props => (props.mode === "dark" ? colors.white : colors.grey800)};

  ${media.fullWidth} {
    gap: 24px;
  }

  ${media.tablet} {
    gap: 2.344vw;
  }

  ${media.mobile} {
    gap: 2.336vw;
  }
`
const TestimonialDiv = styled.div`
  display: flex;
  flex-direction: row;
  height: auto;
  min-width: 101%;
  gap: 0.694vw;
  opacity: 0;

  ${media.fullWidth} {
    gap: 10px;
  }

  ${media.tablet} {
    gap: 2.344vw;
  }

  ${media.mobile} {
    gap: 2.336vw;
  }
`
const TestimonialsContainer = styled.div`
  display: flex;
  flex-direction: row;
  overflow: hidden;
  height: auto;
  gap: 1.389vw;
  margin-left: -30px;

  ${media.fullWidth} {
    gap: 20px;
  }

  ${media.tablet} {
    gap: 1.953vw;
  }

  ${media.mobile} {
    gap: 4.673vw;
  }
`
const CarouselContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  height: auto;

  width: 71.389vw;

  ${media.fullWidth} {
    width: 1028px;
  }

  ${media.tablet} {
    align-items: center;

    width: 89.199vw;
  }

  ${media.mobile} {
    align-items: center;

    width: 94.393vw;
  }
`
const DesignImage = styled.img`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 23.542vw;

  ${media.fullWidth} {
    width: 339px;
  }
  ${media.tablet} {
    width: 30.664vw;
  }
  ${media.mobile} {
    width: 33.879vw;
  }
`
const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  height: auto;
  width: 100%;
  padding: 2.778vw 13.056vw 4.167vw 10.278vw;
  background: ${props =>
    props.mode === "dark"
      ? colors.darkPurpleGradient
      : colors.lightPurpleGradient};

  ${media.fullWidth} {
    width: 100vw;
    padding: 40px 188px 60px 148px;
  }

  ${media.tablet} {
    padding: 5.859vw 7.031vw;
  }

  ${media.mobile} {
    padding: 9.346vw 2.804vw 4.673vw 6.542vw;
  }
`
